import Phaser from 'phaser';

interface IslandData {
  id: string;
  x: number;
  y: number;
  radius: number;
  type: string;
  biome: string;
  ownerId: string | null;
  ownerType: string | null;
  ownerName: string | null;
  ownerShipColor: string | null;
  factionId: string | null;
  permanentName: string | null;
  customName: string | null;
  claimRadius: number | null;
  isPermanent: boolean;
  assignedFaction?: string | null;
}

const FACTION_COLORS: Record<string, number> = {
  navy: 0x3b82f6,
  pirate: 0xef4444,
  merchant: 0x10b981,
  smuggler: 0x8b5cf6,
  regime: 0xe74c3c, // The Regime (Arcadia) - distinct red
};

const UNCLAIMED_COLOR = 0x475569;
const PLAYER_CLAIMED_COLOR = 0x06b6d4;
const REGIME_COLOR = 0xe74c3c;

export class FactionMapScene extends Phaser.Scene {
  private islands: IslandData[] = [];
  private islandGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();
  private selectedIsland: IslandData | null = null;
  private isLoaded = false;
  private isInitializing = false;

  constructor() {
    super({ key: 'FactionMapScene' });
  }

  preload(): void {
    // No assets needed
  }

  create(): void {
    console.log('[FACTION MAP] Scene created, isLoaded:', this.isLoaded, 'isInitializing:', this.isInitializing);
    this.setupCameraControls();
    this.cameras.main.setBackgroundColor(0x0a0e1a);
    
    // Only load data once, and prevent concurrent initialization
    if (!this.isLoaded && !this.isInitializing) {
      console.log('[FACTION MAP] First creation, loading data');
      this.isInitializing = true;
      this.loadData();
    } else if (this.isLoaded) {
      // Already loaded, emit event immediately
      console.log('[FACTION MAP] Scene recreated, emitting mapLoaded immediately');
      this.events.emit('mapLoaded', this.islands.length);
    } else {
      console.log('[FACTION MAP] Already initializing, skipping');
    }
  }

  private async loadData(): Promise<void> {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      console.log('[FACTION MAP] Fetching from:', apiUrl);
      
      const response = await fetch(`${apiUrl}/api/faction-warfare/map-data`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      this.islands = data.islands || [];
      console.log('[FACTION MAP] Loaded', this.islands.length, 'islands from API');
      console.log('[FACTION MAP] API response summary:', {
        totalIslands: data.totalIslands,
        claimedIslands: data.claimedIslands,
        factionIslands: data.factionIslands
      });
      
      // Debug: Log faction distribution
      const factionCounts: Record<string, number> = {};
      this.islands.forEach(island => {
        let faction = 'unclaimed';
        // Use assignedFaction as primary source of truth (from API)
        if (island.assignedFaction) {
          faction = island.assignedFaction;
        } else if (island.ownerType === 'faction_regime') {
          faction = 'regime';
        } else if (island.factionId) {
          faction = island.factionId;
        } else if (island.ownerId?.startsWith('faction_')) {
          faction = island.ownerId.replace('faction_', '');
        } else if (island.ownerId) {
          faction = 'player';
        }
        factionCounts[faction] = (factionCounts[faction] || 0) + 1;
      });
      console.log('[FACTION MAP] Client-side faction distribution:', factionCounts);
      console.log('[FACTION MAP] Total territories counted:', Object.values(factionCounts).reduce((a, b) => a + b, 0));
      
      // Log islands with assignedFaction
      const assignedFactionIslands = this.islands.filter(i => i.assignedFaction);
      console.log('[FACTION MAP] Islands with assignedFaction:', assignedFactionIslands.length);
      if (assignedFactionIslands.length > 0) {
        console.log('[FACTION MAP] Sample assigned islands:', assignedFactionIslands.slice(0, 5).map(i => ({ id: i.id, assignedFaction: i.assignedFaction, ownerType: i.ownerType, ownerId: i.ownerId })));
      }
      console.log('[FACTION MAP] Sample islands:', this.islands.slice(0, 5).map(i => ({ id: i.id, factionId: i.factionId, assignedFaction: i.assignedFaction, ownerType: i.ownerType, ownerId: i.ownerId })));
      
      this.isLoaded = true;
      
      // Wait for next frame to ensure scene is fully initialized
      this.events.once(Phaser.Scenes.Events.UPDATE, () => {
        console.log('[FACTION MAP] Scene UPDATE event fired, drawing map');
        this.drawMap();
        this.setupInput();
        console.log('[FACTION MAP] Emitting mapLoaded event');
        this.events.emit('mapLoaded', this.islands.length);
      });
    } catch (error) {
      console.error('[FACTION MAP] Error:', error);
      console.log('[FACTION MAP] Emitting mapError event');
      this.events.emit('mapError', error);
    }
  }

  private drawMap(): void {
    if (this.islands.length === 0) return;
    
    // Clear previous graphics
    this.islandGraphics.forEach(g => g.destroy());
    this.islandGraphics.clear();
    
    const xs = this.islands.map(i => i.x);
    const ys = this.islands.map(i => i.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    
    const worldWidth = maxX - minX;
    const worldHeight = maxY - minY;
    
    const padding = 100;
    const scaleX = (this.scale.width - padding * 2) / worldWidth;
    const scaleY = (this.scale.height - padding * 2) / worldHeight;
    const scale = Math.min(scaleX, scaleY);
    
    const mapData = { minX, minY, scale, padding };
    
    // Cluster nearby islands for better visual grouping
    const clusterSize = 50; // pixels - islands within this distance are grouped
    const clusters = new Map<string, IslandData[]>();
    
    this.islands.forEach(island => {
      const screenX = (island.x - minX) * scale + padding;
      const screenY = (island.y - minY) * scale + padding;
      const clusterKey = `${Math.floor(screenX / clusterSize)},${Math.floor(screenY / clusterSize)}`;
      
      if (!clusters.has(clusterKey)) {
        clusters.set(clusterKey, []);
      }
      clusters.get(clusterKey)!.push(island);
    });
    
    // Draw islands with clustering awareness
    this.islands.forEach(island => {
      const screenX = (island.x - minX) * scale + padding;
      const screenY = (island.y - minY) * scale + padding;
      const clusterKey = `${Math.floor(screenX / clusterSize)},${Math.floor(screenY / clusterSize)}`;
      const clusterCount = clusters.get(clusterKey)?.length || 1;
      
      // Adjust size based on cluster density
      const baseSizeMultiplier = Math.max(0.8, 1.5 - clusterCount * 0.1);
      const size = Math.max(6, island.radius * scale * 0.12 * baseSizeMultiplier);
      
      const color = this.getColor(island);
      
      const graphics = this.add.graphics();
      graphics.fillStyle(color, 1);
      graphics.fillRect(screenX - size / 2, screenY - size / 2, size, size);
      
      // Add subtle border for player territories (cyan glow) or regime territories (red glow)
      if (island.ownerType === 'faction_regime') {
        graphics.lineStyle(2, 0xff6b6b, 0.8);
        graphics.strokeRect(screenX - size / 2, screenY - size / 2, size, size);
      } else if (island.ownerId && !island.factionId) {
        graphics.lineStyle(2, 0x22d3ee, 0.8);
        graphics.strokeRect(screenX - size / 2, screenY - size / 2, size, size);
      }
      
      // Make interactive
      const hitArea = new Phaser.Geom.Rectangle(screenX - size / 2 - 3, screenY - size / 2 - 3, size + 6, size + 6);
      graphics.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
      
      // Hover effect
      graphics.on('pointerover', () => {
        graphics.clear();
        graphics.fillStyle(color, 0.8);
        graphics.fillRect(screenX - size / 2, screenY - size / 2, size, size);
        
        // White border on hover for all islands
        graphics.lineStyle(2, 0xffffff, 1);
        graphics.strokeRect(screenX - size / 2, screenY - size / 2, size, size);
        
        // Show label on hover for named islands or faction territories
        let labelText = island.permanentName || island.customName;
        
        if (!labelText) {
          if (island.ownerType === 'faction_regime') {
            labelText = 'REGIME TERRITORY';
          } else if (island.ownerId && island.ownerId.startsWith('faction_')) {
            const factionName = island.ownerId.replace('faction_', '');
            labelText = `${factionName.toUpperCase()} TERRITORY`;
          } else if (island.factionId) {
            labelText = `${island.factionId.toUpperCase()} TERRITORY`;
          } else if (island.ownerId) {
            labelText = 'PLAYER CLAIMED';
          }
        } else {
          labelText = labelText.toUpperCase();
        }
        
        if (labelText) {
          const label = this.add.text(screenX, screenY - size / 2 - 15, labelText, {
            fontSize: '11px',
            fontFamily: 'Sometype Mono, monospace',
            color: '#ffffff',
            backgroundColor: '#0a0e1a',
            padding: { x: 4, y: 2 },
          });
          label.setOrigin(0.5, 1);
          label.setDepth(100);
          graphics.setData('label', label);
        }
      });
      
      graphics.on('pointerout', () => {
        graphics.clear();
        graphics.fillStyle(color, 1);
        graphics.fillRect(screenX - size / 2, screenY - size / 2, size, size);
        
        // Restore border for territories
        if (island.ownerType === 'faction_regime') {
          graphics.lineStyle(2, 0xff6b6b, 0.8);
          graphics.strokeRect(screenX - size / 2, screenY - size / 2, size, size);
        } else if (island.ownerId && !island.factionId) {
          graphics.lineStyle(2, 0x22d3ee, 0.8);
          graphics.strokeRect(screenX - size / 2, screenY - size / 2, size, size);
        }
        
        const label = graphics.getData('label');
        if (label) label.destroy();
      });
      
      graphics.on('pointerdown', () => {
        console.log('[FACTION MAP] Island clicked:', island.id, island.permanentName || island.customName || 'unnamed');
        this.selectIsland(island);
      });
      
      this.islandGraphics.set(island.id, graphics);
    });
    
    const centerX = (minX + maxX) / 2 * scale + padding;
    const centerY = (minY + maxY) / 2 * scale + padding;
    this.cameras.main.centerOn(centerX, centerY);
    this.cameras.main.setZoom(0.8);
    
    this.registry.set('mapData', mapData);
  }

  private setupCameraControls(): void {
    // Enable camera zoom with mouse wheel
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: any[], _deltaX: number, deltaY: number) => {
      const zoomAmount = deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Phaser.Math.Clamp(this.cameras.main.zoom + zoomAmount, 0.3, 2);
      this.cameras.main.setZoom(newZoom);
    });

    // Enable camera panning with right mouse drag
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        isDragging = true;
        dragStartX = pointer.x;
        dragStartY = pointer.y;
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (isDragging) {
        const dx = pointer.x - dragStartX;
        const dy = pointer.y - dragStartY;
        this.cameras.main.scrollX -= dx;
        this.cameras.main.scrollY -= dy;
        dragStartX = pointer.x;
        dragStartY = pointer.y;
      }
    });

    this.input.on('pointerup', () => {
      isDragging = false;
    });
  }

  private setupInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Click on empty space deselects
      if (!this.selectedIsland) return;
      const graphics = this.islandGraphics.get(this.selectedIsland.id);
      if (graphics && !graphics.input?.hitArea.contains(pointer.x, pointer.y)) {
        this.selectedIsland = null;
        this.events.emit('islandDeselected');
      }
    });
  }

  private selectIsland(island: IslandData): void {
    this.selectedIsland = island;
    console.log('[FACTION MAP] Selected island:', island.id);
    this.events.emit('islandSelected', island);
  }

  private getColor(island: IslandData): number {
    // Priority: assignedFaction (from TerritoryClaim) > regime > player > unclaimed
    // This matches the stats calculation in the wrapper
    
    // Check assignedFaction first (primary source of truth from TerritoryClaim API)
    if (island.assignedFaction) {
      if (island.assignedFaction === 'regime') {
        return REGIME_COLOR;
      }
      if (FACTION_COLORS[island.assignedFaction]) {
        return FACTION_COLORS[island.assignedFaction];
      }
    }
    
    // Check if this is regime territory (fallback)
    if (island.ownerType === 'faction_regime') {
      return REGIME_COLOR;
    }
    
    // Player-owned territory (has owner but no assignedFaction)
    if (island.ownerId) {
      return PLAYER_CLAIMED_COLOR;
    }
    
    // Unclaimed
    return UNCLAIMED_COLOR;
  }

  public getIslands(): IslandData[] {
    return this.islands;
  }

  update(): void {
    // Placeholder
  }
}
